import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { FileText, Download, Star, Users, ArrowRight, CheckCircle } from 'lucide-react';

export const Home = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  const features = language === 'en' ? [
    { icon: <FileText className="w-6 h-6" />, title: 'Professional Templates', desc: 'Outsourcing and Perm' },
    { icon: <Download className="w-6 h-6" />, title: 'Export to Word', desc: 'Download in .docx format' },
    { icon: <Star className="w-6 h-6" />, title: 'Visual Competencies', desc: 'Scale from 1 to 5 points' },
    { icon: <Users className="w-6 h-6" />, title: 'Bilingual', desc: 'Portuguese and English' }
  ] : [
    { icon: <FileText className="w-6 h-6" />, title: 'Templates Profissionais', desc: 'Outsourcing e Perm' },
    { icon: <Download className="w-6 h-6" />, title: 'Exportar Word', desc: 'Download em formato .docx' },
    { icon: <Star className="w-6 h-6" />, title: 'Competências Visuais', desc: 'Escala de 1 a 5 pontos' },
    { icon: <Users className="w-6 h-6" />, title: 'Bilingue', desc: 'Português e Inglês' }
  ];

  const texts = language === 'en' ? {
    badge: 'Professional CV Builder',
    heroTitle: 'Create your Professional CV',
    heroSubtitle: 'Resume creation tool for professional recruitment',
    getStarted: 'Get Started',
    login: 'Login',
    features: 'Features',
    featuresSubtitle: 'Everything you need to create professional resumes',
    chooseTemplate: 'Choose Template',
    outsourcingFeature1: 'Availability',
    outsourcingFeature2: '6 main competencies',
    ctaTitle: 'Ready to create your CV?',
    ctaSubtitle: 'Start now and have your professional resume in minutes',
    footer: 'All rights reserved.'
  } : {
    badge: 'CV Builder Profissional',
    heroTitle: 'Crie o seu CV Profissional',
    heroSubtitle: 'Ferramenta de criação de currículos para recrutamento profissional',
    getStarted: 'Começar Agora',
    login: 'Entrar',
    features: 'Funcionalidades',
    featuresSubtitle: 'Tudo o que precisa para criar currículos profissionais',
    chooseTemplate: 'Escolha o Template',
    outsourcingFeature1: 'Disponibilidade',
    outsourcingFeature2: '6 competências principais',
    ctaTitle: 'Pronto para criar o seu CV?',
    ctaSubtitle: 'Comece já e tenha o seu currículo profissional em minutos',
    footer: 'Todos os direitos reservados.'
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-gradient py-20 px-4" data-testid="hero-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#f0fdfa] rounded-full text-[#1E3C3C] text-sm font-medium mb-6">
                <CheckCircle className="w-4 h-4" />
                {texts.badge}
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0f172a] mb-6" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                {texts.heroTitle}
              </h1>
              <p className="text-lg text-[#64748b] mb-8 max-w-lg">
                {texts.heroSubtitle}
              </p>
              <div className="flex flex-wrap gap-4">
                <Button
                  className="btn-primary px-8 py-6 text-lg"
                  onClick={() => navigate(user ? '/cv/new' : '/register')}
                  data-testid="get-started-btn"
                >
                  {texts.getStarted}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                {!user && (
                  <Button
                    variant="outline"
                    className="px-8 py-6 text-lg border-2 border-[#1E3C3C] text-[#1E3C3C] hover:bg-[#f0fdfa]"
                    onClick={() => navigate('/login')}
                    data-testid="login-hero-btn"
                  >
                    {texts.login}
                  </Button>
                )}
              </div>
            </div>
            <div className="hidden lg:block">
              <div className="relative">
                <div className="cv-paper rounded-xl p-8 animate-fade-in" style={{ animationDelay: '0.2s' }}>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 rounded-full bg-[#f0fdfa] flex items-center justify-center">
                      <FileText className="w-8 h-8 text-[#1E3C3C]" />
                    </div>
                    <div>
                      <div className="h-4 bg-[#e2e8f0] rounded w-32 mb-2"></div>
                      <div className="h-3 bg-[#f1f5f9] rounded w-24"></div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="h-3 bg-[#e2e8f0] rounded w-full"></div>
                    <div className="h-3 bg-[#f1f5f9] rounded w-3/4"></div>
                    <div className="h-3 bg-[#e2e8f0] rounded w-5/6"></div>
                  </div>
                  <div className="mt-6 pt-6 border-t border-[#e2e8f0]">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="h-3 bg-[#1E3C3C] rounded w-20"></div>
                      <div className="flex gap-1">
                        {[1,2,3,4,5].map(i => (
                          <span key={i} className={`text-sm ${i <= 4 ? 'text-[#1E3C3C]' : 'text-[#e2e8f0]'}`}>●</span>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="h-3 bg-[#FFD700] rounded w-16"></div>
                      <div className="flex gap-1">
                        {[1,2,3,4,5].map(i => (
                          <span key={i} className={`text-sm ${i <= 3 ? 'text-[#1E3C3C]' : 'text-[#e2e8f0]'}`}>●</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-[#FFD700] rounded-xl opacity-20"></div>
                <div className="absolute -top-4 -left-4 w-16 h-16 bg-[#1E3C3C] rounded-xl opacity-20"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 bg-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold text-[#0f172a] mb-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              {texts.features}
            </h2>
            <p className="text-[#64748b] max-w-2xl mx-auto">
              {texts.featuresSubtitle}
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card 
                key={`feat-${feature.title}`} 
                className="card-hover border-2 border-transparent hover:border-[#1E3C3C]/20"
                data-testid={`feature-card-${index}`}
              >
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-[#f0fdfa] flex items-center justify-center text-[#1E3C3C] mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="font-semibold text-[#0f172a] mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    {feature.title}
                  </h3>
                  <p className="text-sm text-[#64748b]">
                    {feature.desc}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Templates Section */}
      <section className="py-20 px-4 bg-[#f8fafc]" data-testid="templates-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold text-[#0f172a] mb-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              {texts.chooseTemplate}
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
            {/* Outsourcing Template */}
            <Card 
              className="template-card group cursor-pointer"
              onClick={() => navigate(user ? '/cv/new?template=outsourcing' : '/register')}
              data-testid="template-outsourcing"
            >
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-[#1E3C3C] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-[#0f172a]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  Outsourcing
                </h3>
              </CardContent>
            </Card>

            {/* Perm Template */}
            <Card 
              className="template-card group cursor-pointer"
              onClick={() => navigate(user ? '/cv/new?template=perm' : '/register')}
              data-testid="template-perm"
            >
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-[#FFD700] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <FileText className="w-6 h-6 text-[#1a202c]" />
                </div>
                <h3 className="text-lg font-semibold text-[#0f172a]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  Perm
                </h3>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-[#1E3C3C]" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            {texts.ctaTitle}
          </h2>
          <p className="text-white/80 mb-8 text-lg">
            {texts.ctaSubtitle}
          </p>
          <Button
            className="btn-secondary px-8 py-6 text-lg"
            onClick={() => navigate(user ? '/cv/new' : '/register')}
            data-testid="cta-btn"
          >
            {texts.getStarted}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 bg-[#0f172a]" data-testid="footer">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center">
            <img src="/evolutt_logo_white.png" alt="Evolutt" className="h-7 w-auto" />
          </div>
          <p className="text-[#64748b] text-sm">
            © 2024 Evolutt. {texts.footer}
          </p>
        </div>
      </footer>
    </div>
  );
};
